import { useState, useEffect } from 'react';
import { track, TRACKING_EVENTS } from '../../utils/tracking';
import { buildApplicationUrl } from '../../utils/buildUrl';

interface FormAnswers {
  timeInBusiness?: string;
  monthlyRevenue?: string;
  industry?: string;
  fundingPurpose?: string;
  fundingAmount?: string;
  goal?: string;
  creditScore?: string;
}

interface StepperFormProps {
  onUpdate?: (answers: FormAnswers) => void;
  onComplete?: (answers: FormAnswers) => void;
  onAnswersChange?: (answers: FormAnswers, currentQuestionId?: string) => void;
  cityContext?: string;
}

const questions = [
  {
    id: 'timeInBusiness',
    question: 'How long has your business been operating?',
    options: [
      { value: '<4mo', label: 'Less than 4 months' },
      { value: '4-12mo', label: '4-12 months' },
      { value: '12-24mo', label: '12-24 months' },
      { value: '24+mo', label: '24+ months' }
    ]
  },
  {
    id: 'monthlyRevenue',
    question: 'What is your average monthly revenue?',
    options: [
      { value: '<10k', label: 'Under $10,000' },
      { value: '10-29k', label: '$10,000 - $29,999' },
      { value: '30-49k', label: '$30,000 - $49,999' },
      { value: '50-99k', label: '$50,000 - $99,999' },
      { value: '100k+', label: '$100,000+' }
    ]
  },
  {
    id: 'industry',
    question: 'What industry is your business in?',
    type: 'dropdown',
    options: [
      { value: 'retail', label: 'Retail' },
      { value: 'restaurant', label: 'Restaurant / Food Service' },
      { value: 'construction', label: 'Construction' },
      { value: 'healthcare', label: 'Healthcare' },
      { value: 'professional-services', label: 'Professional Services' },
      { value: 'manufacturing', label: 'Manufacturing' },
      { value: 'transportation', label: 'Transportation / Logistics' },
      { value: 'real-estate', label: 'Real Estate' },
      { value: 'technology', label: 'Technology' },
      { value: 'hospitality', label: 'Hospitality' },
      { value: 'automotive', label: 'Automotive' },
      { value: 'other', label: 'Other' }
    ]
  },
  {
    id: 'fundingPurpose',
    question: 'What is the primary purpose for funding?',
    type: 'dropdown',
    options: [
      { value: 'working-capital', label: 'Working Capital' },
      { value: 'equipment', label: 'Equipment Purchase' },
      { value: 'expansion', label: 'Business Expansion' },
      { value: 'inventory', label: 'Inventory Purchase' },
      { value: 'real-estate', label: 'Real Estate / Property' },
      { value: 'debt-consolidation', label: 'Debt Consolidation' },
      { value: 'marketing', label: 'Marketing / Advertising' },
      { value: 'hiring', label: 'Hiring / Payroll' },
      { value: 'other', label: 'Other' }
    ]
  },
  {
    id: 'fundingAmount',
    question: 'How much funding do you need?',
    type: 'input',
    inputType: 'number',
    placeholder: '50000',
    min: 50000,
    defaultValue: '50000'
  },
  {
    id: 'goal',
    question: 'What type of funding product are you interested in?',
    options: [
      { value: 'mca', label: 'Merchant Cash Advance' },
      { value: 'line-of-credit', label: 'Line of Credit' },
      { value: 'term-loan', label: 'Term Loan' },
      { value: 'equipment', label: 'Equipment Financing' },
      { value: 'sba-cre', label: 'SBA / Commercial Real Estate' }
    ]
  },
  {
    id: 'creditScore',
    question: 'What is your approximate credit score? (Optional)',
    options: [
      { value: '<600', label: 'Under 600' },
      { value: '600-649', label: '600-649' },
      { value: '650-699', label: '650-699' },
      { value: '700+', label: '700+' }
    ]
  }
];

export default function StepperForm({
  onUpdate,
  onComplete,
  onAnswersChange,
  cityContext
}: StepperFormProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<FormAnswers>({});
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualificationReason, setDisqualificationReason] = useState('');

  const currentQuestion = questions[currentStep];
  const currentAnswer = answers[currentQuestion.id as keyof FormAnswers];
  const totalSteps = questions.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Initialize funding amount with default value when reaching that step
  useEffect(() => {
    if (currentQuestion.id === 'fundingAmount' && !answers.fundingAmount && currentQuestion.defaultValue) {
      const newAnswers = { ...answers, fundingAmount: currentQuestion.defaultValue };
      setAnswers(newAnswers);
      
      if (onUpdate) {
        onUpdate(newAnswers);
      }
      
      if (onAnswersChange) {
        onAnswersChange(newAnswers, currentQuestion.id);
      }
    }
  }, [currentStep]);

  // Notify parent of current question context when step changes
  useEffect(() => {
    if (onAnswersChange) {
      onAnswersChange(answers, currentQuestion.id);
    }
  }, [currentStep]);

  // Check for disqualification
  const checkDisqualification = (field: keyof FormAnswers, value: string) => {
    // Less than 4 months in business - DISQUALIFIED
    if (field === 'timeInBusiness' && value === '<4mo') {
      setIsDisqualified(true);
      setDisqualificationReason('Unfortunately, we require businesses to have been operating for at least 4 months to qualify for funding through our platform.');
      return true;
    }

    // Under $10,000 monthly revenue - DISQUALIFIED
    if (field === 'monthlyRevenue' && value === '<10k') {
      setIsDisqualified(true);
      setDisqualificationReason('Unfortunately, we require businesses to have at least $10,000 in monthly revenue to qualify for funding through our platform.');
      return true;
    }

    // Under $50,000 funding amount - DISQUALIFIED
    if (field === 'fundingAmount' && value) {
      const amount = parseInt(value);
      if (!isNaN(amount) && amount < 50000) {
        setIsDisqualified(true);
        setDisqualificationReason('Unfortunately, our platform focuses on funding requests of $50,000 or more. For smaller amounts, we recommend exploring alternative financing options.');
        return true;
      }
    }

    return false;
  };

  const handleAnswerChange = (field: keyof FormAnswers, value: string) => {
    const newAnswers = { ...answers, [field]: value };
    setAnswers(newAnswers);
    
    // Track answer
    track(`answered_${field}`, { value, city: cityContext });
    
    // Check for disqualification
    const isDisqualifiedNow = checkDisqualification(field, value);
    
    if (!isDisqualifiedNow) {
      // Call onUpdate callback
      if (onUpdate) {
        onUpdate(newAnswers);
      }
      
      // Call onAnswersChange with current question context
      if (onAnswersChange) {
        onAnswersChange(newAnswers, field);
      }
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
      track(TRACKING_EVENTS.STEP_COMPLETE, { 
        step: currentStep + 1,
        city: cityContext 
      });
    } else {
      // Form complete
      if (onComplete) {
        onComplete(answers);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (step: number) => {
    // LOCKED SEQUENCE: Can only go to steps that have been answered
    // Can go back to any previous step, but cannot skip ahead
    if (step <= currentStep) {
      // Allow going back to any answered step
      setCurrentStep(step);
    } else {
      // Check if all previous steps are answered
      let canJump = true;
      for (let i = currentStep; i < step; i++) {
        const questionId = questions[i].id as keyof FormAnswers;
        if (!answers[questionId] && i !== 6) { // Step 6 (credit) is optional
          canJump = false;
          break;
        }
      }
      
      if (canJump) {
        setCurrentStep(step);
      }
      // If cannot jump, do nothing (locked)
    }
  };

  const handleOptionClick = (value: string) => {
    handleAnswerChange(currentQuestion.id as keyof FormAnswers, value);
  };

  const handleInputChange = (value: string) => {
    // Don't trigger disqualification check on every keystroke
    // Just update the answer
    const newAnswers = { ...answers, [currentQuestion.id]: value };
    setAnswers(newAnswers);
    
    // Call onUpdate callback
    if (onUpdate) {
      onUpdate(newAnswers);
    }
    
    // Call onAnswersChange with current question context
    if (onAnswersChange) {
      onAnswersChange(newAnswers, currentQuestion.id);
    }
  };

  const handleInputBlur = () => {
    // Check disqualification when user leaves the input field
    const value = answers[currentQuestion.id as keyof FormAnswers];
    if (value) {
      checkDisqualification(currentQuestion.id as keyof FormAnswers, value);
      track(`answered_${currentQuestion.id}`, { value, city: cityContext });
    }
  };

  const canGoNext = currentAnswer || currentStep === 6; // Step 7 (credit) is optional

  const handleNextClick = () => {
    // For funding amount input, check disqualification before proceeding
    if (currentQuestion.id === 'fundingAmount') {
      const value = answers.fundingAmount;
      if (value) {
        const isDisqualifiedNow = checkDisqualification('fundingAmount', value);
        if (isDisqualifiedNow) {
          return; // Stop here if disqualified
        }
      }
    }

    if (currentStep === totalSteps - 1) {
      // Last step - redirect to external application
      setIsRedirecting(true);
      
      track(TRACKING_EVENTS.STEP_COMPLETE, { 
        step: currentStep + 1,
        city: cityContext,
        form_complete: true
      });

      // Build URL with tracking parameters and redirect
      const redirectUrl = buildApplicationUrl(
        answers,
        'qualified',
        cityContext,
        answers.industry,
        answers.fundingPurpose,
        answers.fundingAmount
      );
      
      track(TRACKING_EVENTS.REDIRECT_CLICK, {
        city: cityContext,
        url: redirectUrl,
        ...answers
      });

      // Delay slightly to show loading state
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 1500);
    } else {
      // Move to next step
      if (currentStep < totalSteps - 1) {
        setCurrentStep(currentStep + 1);
        track(TRACKING_EVENTS.STEP_COMPLETE, { 
          step: currentStep + 1,
          city: cityContext 
        });
      }
    }
  };

  // Hidden continue button for summary section to trigger
  const handleRedirect = () => {
    // Build URL with all tracking parameters
    const redirectUrl = buildApplicationUrl(
      answers,
      'qualified', // tier
      cityContext,
      answers.industry,
      answers.fundingPurpose,
      answers.fundingAmount
    );
    
    track(TRACKING_EVENTS.REDIRECT_CLICK, {
      city: cityContext,
      url: redirectUrl,
      ...answers
    });
    
    window.location.href = redirectUrl;
  };

  const handleRestartForm = () => {
    setIsDisqualified(false);
    setDisqualificationReason('');
    setAnswers({});
    setCurrentStep(0);
  };

  // Disqualification Screen
  if (isDisqualified) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <i className="ri-close-circle-line text-red-600 text-3xl sm:text-4xl"></i>
          </div>
          
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
            Unable to Proceed
          </h2>
          
          <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 px-2">
            {disqualificationReason}
          </p>
          
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
              Alternative Options
            </h3>
            <ul className="text-left space-y-2 text-sm sm:text-base text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-blue-600 text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                <span>Consider building your business revenue and reapplying in the future</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-blue-600 text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                <span>Explore microloans or small business grants in your area</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-blue-600 text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                <span>Look into crowdfunding or angel investor opportunities</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-blue-600 text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                <span>Contact your local Small Business Development Center (SBDC) for guidance</span>
              </li>
            </ul>
          </div>
          
          <button
            onClick={handleRestartForm}
            className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-restart-line mr-2"></i>
            Start Over
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Loading Overlay */}
      {isRedirecting && (
        <div className="fixed inset-0 bg-white/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="text-center px-4 sm:px-6 max-w-md">
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6">
              <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-red-600 rounded-full border-t-transparent animate-spin"></div>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Preparing Your Application
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">
              Redirecting you to our secure partner portal...
            </p>
            <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-500">
              <i className="ri-shield-check-line text-green-600"></i>
              <span>256-bit SSL Encrypted</span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
        {/* Progress Bar */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm font-medium text-gray-700">
              Step {currentStep + 1} of {totalSteps}
            </span>
            <span className="text-xs sm:text-sm text-gray-500">{Math.round(progress)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-red-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step Navigation - LOCKED SEQUENCE - Horizontal scroll on mobile */}
        <div className="mb-6 sm:mb-8 -mx-4 sm:mx-0 px-4 sm:px-0">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {questions.map((q, index) => {
              const isAnswered = answers[q.id as keyof FormAnswers];
              const isCurrent = index === currentStep;
              const isPast = index < currentStep;
              const isLocked = index > currentStep && !isAnswered;

              return (
                <button
                  key={q.id}
                  onClick={() => handleStepClick(index)}
                  disabled={isLocked}
                  className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-sm font-medium transition-all whitespace-nowrap ${
                    isCurrent
                      ? 'bg-red-600 text-white cursor-pointer'
                      : isAnswered
                      ? 'bg-red-100 text-red-600 hover:bg-red-200 cursor-pointer'
                      : isPast
                      ? 'bg-gray-200 text-gray-600 hover:bg-gray-300 cursor-pointer'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                  }`}
                  title={isLocked ? 'Complete previous steps first' : ''}
                >
                  {isLocked ? (
                    <i className="ri-lock-line text-sm"></i>
                  ) : (
                    index + 1
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Question */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
            {currentQuestion.question}
          </h2>
          {currentStep === 6 && (
            <p className="text-xs sm:text-sm text-gray-600">
              This helps us provide more accurate recommendations, but you can skip this step.
            </p>
          )}
        </div>

        {/* Input Fields (for dropdown and number input) */}
        {currentQuestion.type === 'dropdown' && (
          <div className="mb-6 sm:mb-8">
            <select
              value={currentAnswer || ''}
              onChange={(e) => handleInputChange(e.target.value)}
              className="w-full px-3 sm:px-4 py-3 border-2 border-gray-200 rounded-lg text-sm sm:text-base text-gray-900 font-medium focus:border-red-600 focus:outline-none transition-all cursor-pointer"
            >
              <option value="">Select an option...</option>
              {currentQuestion.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {currentQuestion.type === 'input' && (
          <div className="mb-6 sm:mb-8">
            <div className="relative">
              <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-500 text-base sm:text-lg font-medium">
                $
              </span>
              <input
                type={currentQuestion.inputType}
                value={currentAnswer || currentQuestion.defaultValue || ''}
                onChange={(e) => handleInputChange(e.target.value)}
                onBlur={handleInputBlur}
                placeholder={currentQuestion.placeholder}
                min={currentQuestion.min}
                className="w-full pl-7 sm:pl-8 pr-3 sm:pr-4 py-3 border-2 border-gray-200 rounded-lg text-sm sm:text-base text-gray-900 font-medium focus:border-red-600 focus:outline-none transition-all"
              />
            </div>
            <p className="text-xs sm:text-sm text-red-600 mt-2 font-medium">
              Minimum required: $49,999
            </p>
          </div>
        )}

        {/* Options (for button-based questions) */}
        {!currentQuestion.type && (
          <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
            {currentQuestion.options?.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionClick(option.value)}
                className={`w-full text-left px-4 sm:px-6 py-3 sm:py-4 rounded-lg border-2 transition-all cursor-pointer ${
                  currentAnswer === option.value
                    ? 'border-red-600 bg-red-50 text-gray-900'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-red-600 hover:bg-red-50/50'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-sm sm:text-base">{option.label}</span>
                  {currentAnswer === option.value && (
                    <i className="ri-check-line text-red-600 text-lg sm:text-xl flex-shrink-0"></i>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between gap-3 sm:gap-4">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 rounded-lg text-sm sm:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${
              currentStep === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back
          </button>

          <button
            onClick={handleNextClick}
            disabled={!canGoNext || isRedirecting}
            className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 rounded-lg text-sm sm:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${
              canGoNext && !isRedirecting
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {currentStep === totalSteps - 1 ? (
              isRedirecting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="hidden sm:inline">Redirecting...</span>
                  <span className="sm:hidden">...</span>
                </span>
              ) : (
                <>
                  <span className="hidden sm:inline">Continue to Application</span>
                  <span className="sm:hidden">Continue</span>
                  <i className="ri-external-link-line ml-2"></i>
                </>
              )
            ) : (
              <>
                Next
                <i className="ri-arrow-right-line ml-2"></i>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Hidden button for summary section to trigger redirect */}
      <button
        data-continue-button
        onClick={handleRedirect}
        className="hidden"
      />

      {/* Hide horizontal scrollbar on mobile */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
}
